import React, { useEffect, useState, useRef } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Trash2, ExternalLink, Loader2, ImageIcon, Info,
  Share2, Mail as MailIcon, Save, Ruler,
} from "lucide-react";
import { usePublicSettings } from "@/lib/publicSettings";

/**
 * Iter48 Phase B / Iter54a — Branding & Assets Manager.
 *
 * Manages every visual brand asset and its sizing so the marketplace can be
 * re-skinned without a developer touching code:
 *   • Website Logo, Footer Logo, Favicon, Hero Banner, OG Sharing Image,
 *     Email Header Logo — each with upload, preview, replace, remove, and
 *     direct-URL fallback.
 *   • Logo & header sizing — width (desktop + mobile), header height,
 *     footer logo width, and CSS object-fit display mode.
 *
 * Uploads use `/api/uploads?kind=image` (same object storage as plan media)
 * and then PATCH `settings.company` with the returned URL. Removing an asset
 * clears its URL and the frontend falls back to bundled defaults.
 */

const ASSETS = [
  {
    key: "logo_url",
    label: "Website Logo",
    hint: "Displayed in the site header on every page.",
    recommended: "Wide wordmark · 1600 × 800 PNG · transparent background",
    where: ["Site header (desktop + mobile)", "Fallback for footer / emails when a dedicated asset is missing"],
    aspect: "aspect-[2/1]",
    fallback: "/dhe-logo.png",
    testid: "asset-logo",
  },
  {
    key: "footer_logo_url",
    label: "Footer Logo",
    hint: "Shown on the dark footer strip. Prefer a light-on-transparent variant so it stays legible on forest green.",
    recommended: "1600 × 800 PNG · transparent, light foreground",
    where: ["Site footer only"],
    fallbackNote: "Leave blank to reuse the Website Logo automatically.",
    aspect: "aspect-[2/1]",
    fallback: "",
    testid: "asset-footer-logo",
  },
  {
    key: "favicon_url",
    label: "Favicon",
    hint: "The tiny icon shown on the browser tab and bookmark bar.",
    recommended: "Square 512 × 512 PNG · centered mark · minimum 32 × 32 legibility",
    where: ["Browser tab / bookmarks", "Home-screen icon on iOS + Android when saved to homescreen", "Windows Start / macOS Dock shortcut"],
    aspect: "aspect-square",
    fallback: "",
    testid: "asset-favicon",
    showFavicon: true,  // triggers the "browser tab" mockup preview
  },
  {
    key: "hero_banner_url",
    label: "Homepage Hero Banner",
    hint: "The full-bleed cinematic image behind the homepage hero.",
    recommended: "2400 × 1400 JPG · sharp architectural photo · focal point on the right (copy sits bottom-left)",
    where: ["Homepage hero — full viewport width"],
    aspect: "aspect-[16/9]",
    fallback: "",
    testid: "asset-hero",
    // Iter55c — this asset is only used when zero slides exist in the
    // Hero Media Manager. Flag it clearly so admins don't upload here
    // when they meant to add a slide.
    legacyNote: {
      title: "Legacy fallback",
      body: "This image is only used when the Hero Media Manager has no slides. For multi-slide carousels, videos, per-slide CTAs, scheduling and analytics, use Hero Slides instead.",
      linkHref: "/admin/homepage",
      linkLabel: "Open Hero Slides",
    },
  },
  {
    key: "og_image_url",
    label: "Open Graph / Social Sharing Image",
    hint: "Automatically appears as the preview card when your website is shared on social platforms.",
    recommended: "1200 × 630 PNG or JPG · brand + tagline overlay works best",
    where: ["Facebook", "LinkedIn", "WhatsApp", "Telegram", "X (Twitter)", "iMessage / Slack link previews"],
    aspect: "aspect-[1200/630]",
    fallback: "",
    testid: "asset-og",
    showOgPreview: true,
  },
  {
    key: "email_header_logo_url",
    label: "Email Header Logo",
    hint: "Placed at the top of every transactional email sent from the platform.",
    recommended: "600 × 200 PNG · transparent background · readable in dark + light email clients",
    where: [
      "Order Confirmation",
      "Password Reset",
      "Contact Form Auto-Reply",
      "Newsletter",
      "Invoice Emails",
      "Coming-Soon Launch Notification",
    ],
    aspect: "aspect-[3/1]",
    fallback: "",
    testid: "asset-email",
    showEmailPreview: true,
  },
];

// Iter54a — Logo & header sizing controls. Each row is one CSS-driven
// dimension exposed by settings.company. Add a new row here to expose an
// additional sizing knob to admin (no code changes required elsewhere).
const SIZING_FIELDS = [
  { key: "header_logo_width", label: "Header Logo Width (Desktop)", placeholder: "e.g. 180px", hint: "Maximum width of the logo in the site header on screens ≥ 640px. Leave blank for auto." },
  { key: "header_height",     label: "Header Height",               placeholder: "e.g. 80px",  hint: "Vertical height of the site header row. The logo scales to fit inside this height." },
  { key: "mobile_logo_width", label: "Mobile Logo Width",           placeholder: "e.g. 120px", hint: "Maximum width of the logo on screens < 640px. Falls back to the desktop width if empty." },
  { key: "footer_logo_width", label: "Footer Logo Width",           placeholder: "e.g. 180px", hint: "Maximum width of the logo in the site footer. Leave blank for auto." },
];

const AssetCard = ({ asset, current, companyName, onSave, onRemove, saving }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlDraft, setUrlDraft] = useState(current || "");

  useEffect(() => { setUrlDraft(current || ""); }, [current]);

  const chooseFile = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large", { description: "Please pick an image under 5 MB." });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads?kind=image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onSave(data.url);
      toast.success(`${asset.label} updated`);
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const saveDirectUrl = async () => {
    const v = urlDraft.trim();
    if (!v) return;
    await onSave(v);
    toast.success(`${asset.label} URL saved`);
  };

  const displaySrc = current || asset.fallback;

  return (
    <div className="border border-border bg-card p-6" data-testid={asset.testid}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-xl">{asset.label}</h3>
          <p className="text-sm text-muted-foreground mt-1">{asset.hint}</p>
          {asset.recommended && (
            <div className="mt-3 inline-flex items-start gap-2 text-xs bg-accent/10 text-accent px-2.5 py-1.5" data-testid={`${asset.testid}-recommended`}>
              <Ruler size={12} className="mt-0.5 shrink-0" />
              <span className="font-medium">Recommended: <span className="opacity-80 font-normal">{asset.recommended}</span></span>
            </div>
          )}
          {asset.where && asset.where.length > 0 && (
            <div className="mt-3 text-xs text-muted-foreground" data-testid={`${asset.testid}-where`}>
              <div className="uppercase tracking-[0.18em] text-[10px] opacity-70 mb-1">Where this appears</div>
              <ul className="list-disc list-inside space-y-0.5">
                {asset.where.map((w) => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}
          {asset.fallbackNote && (
            <p className="mt-3 text-xs italic text-muted-foreground/80">{asset.fallbackNote}</p>
          )}
          {asset.legacyNote && (
            <div className="mt-3 border border-amber-500/40 bg-amber-500/10 p-3 text-xs" data-testid={`${asset.testid}-legacy-note`}>
              <div className="font-medium text-amber-700 dark:text-amber-400 uppercase tracking-[0.14em] text-[10px]">
                ⚠ {asset.legacyNote.title}
              </div>
              <p className="mt-1.5 text-muted-foreground">{asset.legacyNote.body}</p>
              {asset.legacyNote.linkHref && (
                <a
                  href={asset.legacyNote.linkHref}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
                  data-testid={`${asset.testid}-legacy-link`}
                >
                  {asset.legacyNote.linkLabel} <ExternalLink size={11} />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Raw preview */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Raw image</div>
          <div className={`${asset.aspect} bg-background border border-border overflow-hidden flex items-center justify-center`}>
            {displaySrc ? (
              <img
                src={displaySrc}
                alt={asset.label}
                className="max-w-full max-h-full object-contain"
                data-testid={`${asset.testid}-preview`}
              />
            ) : (
              <div className="text-muted-foreground text-sm flex flex-col items-center gap-2" data-testid={`${asset.testid}-empty`}>
                <ImageIcon size={28} />
                <span>No image uploaded yet.</span>
              </div>
            )}
          </div>
        </div>

        {/* Context mockup — different per asset type */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">In-context preview</div>
          {asset.showFavicon && (
            <FaviconTabPreview src={displaySrc} companyName={companyName} testid={asset.testid} />
          )}
          {asset.showOgPreview && (
            <OgSocialPreview src={displaySrc} companyName={companyName} testid={asset.testid} />
          )}
          {asset.showEmailPreview && (
            <EmailHeaderPreview src={displaySrc} companyName={companyName} testid={asset.testid} />
          )}
          {!asset.showFavicon && !asset.showOgPreview && !asset.showEmailPreview && (
            <div className={`${asset.aspect} bg-muted/30 border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground text-center px-4`}>
              This asset appears exactly as shown on the left.
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml,image/vnd.microsoft.icon,image/x-icon"
          className="hidden"
          onChange={onFile}
          data-testid={`${asset.testid}-file-input`}
        />
        <Button
          onClick={chooseFile}
          disabled={uploading || saving}
          data-testid={`${asset.testid}-upload-btn`}
          className="rounded-none bg-primary hover:bg-primary/90 h-11"
        >
          {uploading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</> : <><Upload size={14} className="mr-2" /> Upload new</>}
        </Button>
        {current && (
          <>
            <a href={current} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-accent hover:underline px-3" data-testid={`${asset.testid}-open`}>
              <ExternalLink size={12} /> Open
            </a>
            <Button
              onClick={() => onRemove()}
              disabled={saving || uploading}
              variant="outline"
              className="rounded-none h-11 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              data-testid={`${asset.testid}-remove-btn`}
            >
              <Trash2 size={14} className="mr-2" /> Remove
            </Button>
          </>
        )}
      </div>

      <details className="mt-4">
        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Or paste a direct URL</summary>
        <div className="mt-2 flex gap-2">
          <Input
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://…"
            className="rounded-none h-10"
            data-testid={`${asset.testid}-url-input`}
          />
          <Button
            onClick={saveDirectUrl}
            disabled={!urlDraft.trim() || urlDraft.trim() === (current || "") || saving}
            className="rounded-none bg-primary hover:bg-primary/90 h-10"
            data-testid={`${asset.testid}-url-save-btn`}
          >
            Save URL
          </Button>
        </div>
      </details>
    </div>
  );
};

// ── Iter54a — In-context preview mockups ─────────────────────────────────────
// Small, self-contained visual mocks so admins understand exactly where each
// asset is used and how it will look. Each accepts { src, companyName, testid }.

const FaviconTabPreview = ({ src, companyName, testid }) => (
  <div className="bg-muted/40 border border-border p-3" data-testid={`${testid}-mockup`}>
    <div className="bg-background rounded-t-md border border-border overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/60">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
      </div>
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border">
        <div className="flex items-center gap-1.5 bg-background border border-border rounded-t-md px-2.5 py-1.5 max-w-[240px]">
          {src ? (
            <img src={src} alt="" className="w-4 h-4 rounded-sm object-contain" />
          ) : (
            <div className="w-4 h-4 rounded-sm bg-muted flex items-center justify-center">
              <ImageIcon size={10} />
            </div>
          )}
          <span className="text-xs truncate">{companyName || "Dream Home Engineers"}</span>
          <span className="text-muted-foreground text-xs">×</span>
        </div>
      </div>
      <div className="p-6 text-center text-muted-foreground text-xs italic">Browser tab preview</div>
    </div>
    <p className="mt-2 text-[11px] text-muted-foreground">Also used for iOS/Android home-screen shortcuts.</p>
  </div>
);

const OgSocialPreview = ({ src, companyName, testid }) => (
  <div className="bg-muted/40 border border-border p-3" data-testid={`${testid}-mockup`}>
    <div className="bg-background border border-border overflow-hidden">
      <div className="aspect-[1200/630] bg-muted flex items-center justify-center overflow-hidden">
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="text-muted-foreground text-xs flex flex-col items-center gap-1.5">
            <Share2 size={22} />
            <span>Share card preview</span>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-border">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">plans.dreamhomeengineers.com</div>
        <div className="font-display text-sm mt-0.5 truncate">{companyName || "Dream Home Engineers"} — Premium House Plans</div>
        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">Buy premium ready-to-build house plans from Dream Home Engineers…</div>
      </div>
    </div>
    <p className="mt-2 text-[11px] text-muted-foreground">Appears on Facebook · LinkedIn · WhatsApp · Telegram · X · Slack.</p>
  </div>
);

const EmailHeaderPreview = ({ src, companyName, testid }) => (
  <div className="bg-muted/40 border border-border p-3" data-testid={`${testid}-mockup`}>
    <div className="bg-white border border-border shadow-sm">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border/60 bg-muted/40">
        <MailIcon size={13} className="text-muted-foreground" />
        <div className="text-[11px] text-muted-foreground">Your order is confirmed — #DH-MD-00042</div>
      </div>
      <div className="px-6 py-6 border-b border-border/60 bg-background flex items-center justify-center min-h-[100px]">
        {src ? (
          <img src={src} alt="" className="max-h-14 max-w-[200px] object-contain" />
        ) : (
          <div className="text-muted-foreground text-xs flex items-center gap-1.5">
            <ImageIcon size={14} /> No email logo uploaded
          </div>
        )}
      </div>
      <div className="p-4 space-y-1.5">
        <div className="h-2 bg-muted/60 w-3/4 rounded-sm" />
        <div className="h-2 bg-muted/60 w-full rounded-sm" />
        <div className="h-2 bg-muted/60 w-4/5 rounded-sm" />
      </div>
    </div>
    <p className="mt-2 text-[11px] text-muted-foreground">Used at the top of Order Confirmation, Password Reset, Contact Auto-Reply, Newsletter & Invoice emails.</p>
  </div>
);

const AdminBranding = () => {
  const { refresh: reloadPublicSettings } = usePublicSettings();
  const [company, setCompany] = useState(null);
  const [sizingDraft, setSizingDraft] = useState(null); // separate draft for the sizing form
  const [saving, setSaving] = useState(false);
  const [savingSizing, setSavingSizing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/all");
      const c = data.company || {};
      setCompany(c);
      setSizingDraft({
        header_logo_width: c.header_logo_width || "",
        header_height: c.header_height || "80px",
        mobile_logo_width: c.mobile_logo_width || "",
        footer_logo_width: c.footer_logo_width || "",
        logo_display_mode: c.logo_display_mode || "contain",
      });
    } catch (e) {
      toast.error("Failed to load settings", { description: e.response?.data?.detail || "" });
    }
  };
  useEffect(() => { load(); }, []);

  const saveField = async (key, value) => {
    setSaving(true);
    try {
      const patch = { ...(company || {}), [key]: value };
      await api.put("/admin/settings/company", { data: patch });
      setCompany(patch);
      // Refresh public settings so Header/Footer/etc pick up the change live.
      try { await reloadPublicSettings?.(); } catch (_e) { /* non-fatal */ }
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const saveSizing = async () => {
    if (!sizingDraft) return;
    setSavingSizing(true);
    try {
      const patch = { ...(company || {}), ...sizingDraft };
      await api.put("/admin/settings/company", { data: patch });
      setCompany(patch);
      try { await reloadPublicSettings?.(); } catch (_e) {}
      toast.success("Logo sizing saved", { description: "Header and footer updated live." });
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setSavingSizing(false);
    }
  };

  if (!company || !sizingDraft) return <AdminLayout title="Branding & Assets"><div className="text-muted-foreground">Loading…</div></AdminLayout>;

  const companyName = company.name || "Dream Home Engineers";

  return (
    <AdminLayout title="Branding & Assets">
      <div className="max-w-5xl">
        <div className="border border-border bg-accent/5 p-4 mb-8 flex items-start gap-3" data-testid="branding-help">
          <Info size={16} className="text-accent shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            Upload branding assets once and the marketplace uses them everywhere —
            header, footer, favicon, homepage hero, social share previews, and email
            headers. Each card shows an in-context preview so you can see exactly
            where the asset will appear. Removing an asset falls back to the built-in default.
          </p>
        </div>

        {/* Sizing panel — kept above the asset cards so admin sets header
            dimensions first, then uploads logos into those dimensions. */}
        <div className="border border-border bg-card p-6 mb-8" data-testid="branding-sizing">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h3 className="font-display text-xl">Logo & Header Sizing</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Fine-tune how the logos display in the site header and footer without touching code.
                All values accept any CSS unit (<code className="text-xs bg-muted px-1">px</code>, <code className="text-xs bg-muted px-1">rem</code>, <code className="text-xs bg-muted px-1">%</code>).
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SIZING_FIELDS.map((f) => (
              <div key={f.key} data-testid={`sizing-row-${f.key}`}>
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{f.label}</Label>
                <Input
                  value={sizingDraft[f.key] ?? ""}
                  onChange={(e) => setSizingDraft((s) => ({ ...s, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="rounded-none h-11 mt-2"
                  data-testid={`sizing-${f.key}`}
                />
                <p className="text-[11px] text-muted-foreground mt-1.5">{f.hint}</p>
              </div>
            ))}
            <div data-testid="sizing-row-logo_display_mode">
              <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Logo Display Mode</Label>
              <Select
                value={sizingDraft.logo_display_mode || "contain"}
                onValueChange={(v) => setSizingDraft((s) => ({ ...s, logo_display_mode: v }))}
              >
                <SelectTrigger className="rounded-none h-11 mt-2" data-testid="sizing-logo_display_mode">
                  <SelectValue placeholder="Contain" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contain">Contain — fit whole logo (recommended)</SelectItem>
                  <SelectItem value="cover">Cover — fill box, may crop</SelectItem>
                  <SelectItem value="fill">Fill — stretch to box</SelectItem>
                  <SelectItem value="scale-down">Scale-Down — smaller of contain or original</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1.5">Maps directly to CSS <code className="text-xs bg-muted px-1">object-fit</code>. Applies to header and footer logos.</p>
            </div>
          </div>
          <div className="mt-6">
            <Button
              onClick={saveSizing}
              disabled={savingSizing}
              data-testid="sizing-save-btn"
              className="rounded-none bg-primary hover:bg-primary/90 h-11 px-8"
            >
              {savingSizing ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Sizing</>}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6" data-testid="branding-grid">
          {ASSETS.map((a) => (
            <AssetCard
              key={a.key}
              asset={a}
              current={company[a.key] || ""}
              companyName={companyName}
              saving={saving}
              onSave={(url) => saveField(a.key, url)}
              onRemove={() => saveField(a.key, "")}
            />
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBranding;
